import { requestTranscription, setSentiment } from "@/api/endpoints/episodes";
import { setFavorites } from "@/api/endpoints/favorite";
import { markIrrelevant } from "@/api/endpoints/searches";
import { setTag } from "@/api/endpoints/tags";
import { AUDIO, INSTAGRAM_TITLE, Sentiment, SOURCE, TEXT } from "@/constants";
import { useTags } from "@/contexts/TagContext";
import { useI18n } from "@/contexts/i18nContext";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";
import { MentionCard } from "@/types/MentionCard";
import { Organization } from "@/types/Organization";
import { Tag } from "@/types/Tag";
import { copyToClipboard, round } from "@/utils";
import { SENTIMENT } from "@/utils/sentimentOptions";
import { SOURCE_ACTION } from "@/utils/sourceAction";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import StaffBadge from "@components/StaffBadge";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { ButtonGroup } from "@components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectList,
  MultiSelectSearch,
  MultiSelectTrigger,
} from "@components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookmarkIcon,
  CoinsIcon,
  ExternalLinkIcon,
  EyeIcon,
  HashIcon,
  LinkIcon,
  MoreVerticalIcon,
  NewspaperIcon,
  TagIcon,
  Trash2Icon,
  Users2Icon,
  EyeOffIcon,
  PencilLineIcon,
  AlignLeftIcon,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import ShareMentionCard from "./ShareMentionCard";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import InsightBadge from "@components/InsightBadge";
import { isTranscriptionInProgress } from "@/lib/episode";
import FullTranscript from "@components/FullTranscript";
import { useReadOnly } from "../context";

type MentionCardFooterProps = {
  onAfterChangeSentiment: (newSentiment: Sentiment) => void;
  onAfterRequestTranscribe: () => void;
  orgUuid?: Organization["uuid"];
  onAfterDelete: () => void;
  mentionCard: MentionCard;
  tags?: Tag[];
  hideReach?: boolean;
  hidePrValue?: boolean;
  hideSentiment?: boolean;
};

export function MentionCardFooter({
  onAfterChangeSentiment,
  onAfterRequestTranscribe,
  onAfterDelete,
  mentionCard,
  orgUuid,
  tags = [],
  hideReach,
  hidePrValue,
  hideSentiment,
}: MentionCardFooterProps) {
  const readOnly = useReadOnly();
  const { t } = useLingui();
  const { abbreviateCurrency, formatDate, formatNumber } = useI18n();
  const { fetchOrganizationTags } = useTags() || {};

  // Local state
  const [isContentDialogOpen, setIsContentDialogOpen] =
    useState<boolean>(false);

  const [localTags, setLocalTags] = useState<MentionCard["tags"]>(
    mentionCard.tags
  );

  const [localIsFavorited, setLocalIsFavorited] = useState<
    MentionCard["is_favorited"]
  >(mentionCard.is_favorited);

  // Server state
  const queryClient = useQueryClient();

  const { mutate: mutateSetSentiment } = useMutation({
    mutationFn: (newSentiment: Sentiment) =>
      setSentiment(
        mentionCard.mentions.map((m) => m.public_id),
        newSentiment
      ),
    onSuccess: (res, newSentiment) => {
      if (!res.ok) {
        throw new Error();
      }

      onAfterChangeSentiment(newSentiment);
    },
    onError: () => {
      toast.error(t`Misslyckades med att ändra sentiment`);
    },
  });

  const { mutate: mutateSetTags } = useMutation({
    mutationFn: (newTags: Tag["name"][]) =>
      setTag({
        mentionUuids: mentionCard.mentions.map((m) => m.public_id),
        tags: newTags,
      }),
    onSuccess: (res, newTags) => {
      if (!res.ok) {
        throw new Error();
      }

      postTrackingData(EVENT.TAGGED_MENTION, {
        uuid: mentionCard.mentions.map((m) => m.public_id),
        tags: newTags,
      });

      setLocalTags(newTags);

      queryClient.invalidateQueries({
        queryKey: [orgUuid, "tags"],
      });
      fetchOrganizationTags?.();
    },
    onError: () => {
      toast.error(t`Misslyckades med att ändra taggarna`);
    },
  });

  const { mutate: mutateSetFavorites } = useMutation({
    mutationFn: (newIsFavorite: boolean) =>
      setFavorites(
        mentionCard.mentions.map((m) => m.id),
        newIsFavorite
      ),
    onSuccess: (res, newIsFavorite) => {
      if (!res.ok) {
        throw new Error();
      }

      if (newIsFavorite) {
        postTrackingData(EVENT.MARK_FAVOURITE, {
          url: mentionCard.share_url,
          source: mentionCard.episode.audio_url,
        });
      }

      setLocalIsFavorited(newIsFavorite);
      queryClient.invalidateQueries({
        queryKey: [orgUuid, "tags"],
      });
      fetchOrganizationTags?.();
    },
    onError: (_, newIsFavorite) => {
      toast.error(
        newIsFavorite
          ? t`Misslyckades med att spara`
          : t`Misslyckades med att ta bort från sparade omnämnanden`
      );
    },
  });

  const { mutate: mutateMarkAsIrrelevant } = useMutation({
    mutationFn: ({ reason = null }: { reason?: string | null }) =>
      markIrrelevant(
        mentionCard.mentions.map((m) => m.id),
        { reason }
      ),
    onSuccess: (res, variables) => {
      if (!res.ok) {
        throw new Error();
      }

      const reason = variables?.reason;

      if (reason === "excluded_source") {
        const channelName = mentionCard.channel.name;

        toast.success(
          t`Kanalen "${channelName}" kommer inte längre att visas.`,
          {
            description: t`Alla borttagna omnämnanden finns via menyn "..." när pekaren hålls över bevakningens namn.`,
            className: "!w-[650px]",
            closeButton: true,
            duration: Infinity,
            position: "top-left",
          }
        );
      } else {
        toast.success(t`Inlägg borttaget`);
      }

      onAfterDelete();
    },
    onError: () => {
      toast.error(t`Misslyckades med att ta bort inlägget`);
    },
  });

  const { mutate: mutateRequestTranscribe, isPending: isTranscribePending } =
    useMutation({
      mutationFn: () => requestTranscription(mentionCard.episode.pk.toString()),
      onSuccess: (res) => {
        if (!res.ok) throw new Error();
        toast.success(t`Avsnittet transkriberas`);
        onAfterRequestTranscribe();
      },
      onError: () => {
        toast.error(t`Misslyckades med att transkribera avsnittet`);
      },
    });

  // Helpers
  const canRequestTranscribe = useHasFeature(
    FEATURE.HAS_REQUEST_TRANSCRIPT_FEATURE
  );

  const canShowFullTranscripts = useHasFeature(
    FEATURE.HAS_SHOW_FULL_TRANSCRIPT_FEATURE
  );

  const isAudioSource = AUDIO.includes(mentionCard.channel.channel_type);
  const isNewsSource = mentionCard.channel.channel_type === SOURCE.NEWS;
  const hasTextPost = !!mentionCard.text_post?.text?.trim();
  const hasTranscriptMatch = !!mentionCard.grouped_times_and_words;
  const showFullTranscript =
    isAudioSource && hasTranscriptMatch && canShowFullTranscripts;
  const showReadArticle = isNewsSource && hasTextPost && canShowFullTranscripts;
  const hasAudioMentions = mentionCard.mentions.some(
    (m) => typeof m.timing === "number"
  );

  const isTextSource = TEXT.includes(mentionCard.channel.channel_type);

  const isTiktokOrInstagramVideoOrNonText =
    !isTextSource ||
    mentionCard.channel.channel_type === SOURCE.TIKTOK ||
    (mentionCard.channel.channel_type === SOURCE.INSTAGRAM &&
      (mentionCard.episode.title === INSTAGRAM_TITLE.VIDEO ||
        mentionCard.episode.title === INSTAGRAM_TITLE.STORY));

  const transcriptionInProgress = isTranscriptionInProgress(
    mentionCard.episode.transcript_status
  );

  const showRequestTranscription =
    canRequestTranscribe &&
    !!mentionCard.episode.pk &&
    !mentionCard.episode.has_transcript &&
    isTiktokOrInstagramVideoOrNonText;

  const sentimentOption = mentionCard.aggregated_sentiment
    ? SENTIMENT[mentionCard.aggregated_sentiment]
    : undefined;
  const SentimentIcon = sentimentOption?.icon;

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between py-2 px-4 gap-4 border-t">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-1.5 text-sm">
              {formatDate(mentionCard.episode.published_date, "HH:mm, PP")}
            </div>

            {!hideReach && mentionCard.episode.reach ? (
              <div className="flex items-center gap-1.5 text-sm">
                <Users2Icon className="w-3.5" />
                <span data-testid="reach">
                  {formatNumber(mentionCard.episode.reach)}
                </span>
              </div>
            ) : null}

            {!hidePrValue && mentionCard.pr_value ? (
              <div className="flex items-center gap-1.5 text-sm">
                <CoinsIcon className="w-3.5" />
                <p data-testid="episode-pr-value">
                  {abbreviateCurrency(round(mentionCard.pr_value))}
                </p>
              </div>
            ) : null}
          </div>

          {mentionCard.episode.public_url && (
            <Button
              asChild
              size="sm"
              variant="link"
              className="text-blue-500 font-normal max-w-full lg:max-w-none"
            >
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={mentionCard.episode.public_url}
                onClick={() => {
                  postTrackingData(EVENT.EXTERNAL_URL_CLICK, {
                    url: mentionCard.share_url,
                    source: mentionCard.episode.audio_url,
                    context: "audio url",
                  });
                }}
              >
                {i18n._(SOURCE_ACTION[mentionCard.channel.channel_type])}
                <ExternalLinkIcon />
              </a>
            </Button>
          )}

          {mentionCard.toplist.length ? (
            <Badge
              size="lg"
              shape="square"
              variant="fuchsia"
              className="max-w-lg"
            >
              <HashIcon />
              <span className="flex-1 truncate">
                {mentionCard.toplist
                  .map(
                    (toplist) =>
                      `${toplist.place} ${t`i`} ${toplist.source}${toplist.genre ? `: ${toplist.genre}` : ""}`
                  )
                  .join(", ")}
              </span>
            </Badge>
          ) : null}

          {mentionCard.insights?.length
            ? mentionCard.insights.map((insight) => (
                <InsightBadge key={insight.type} insight={insight} />
              ))
            : null}

          {!hideSentiment && readOnly && sentimentOption && SentimentIcon && (
            <div
              className="flex items-center gap-1.5 text-sm"
              data-testid="sentiment-indicator"
            >
              <SentimentIcon className="size-3.5" />
              <span>{i18n._(sentimentOption.label)}</span>
            </div>
          )}

          {!hideSentiment && !readOnly && mentionCard.aggregated_sentiment && (
            <Select
              value={mentionCard.aggregated_sentiment}
              onValueChange={mutateSetSentiment}
            >
              <SelectTrigger
                data-testid="sentiment-indicator"
                withChevron={false}
                className="font-medium"
                size="sm"
              >
                <SelectValue placeholder={t`Sentiment...`} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SENTIMENT).map(
                  ([_, { icon: Icon, label, value }]) => (
                    <SelectItem
                      key={value}
                      value={value}
                      data-testid={`sentiment-${value}`}
                    >
                      <Icon className="text-foreground" />
                      {i18n._(label)}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          )}

          {!readOnly && (
            <MultiSelect
              value={localTags}
              onChange={mutateSetTags}
              getLabel={(tag) => tag}
              onCreate={(newTag) => {
                mutateSetTags([...localTags, newTag]);
              }}
            >
              <MultiSelectTrigger
                placeholder={t`Lägg till tagg`}
                withChevron={false}
                variant="outline"
                icon={TagIcon}
                size="sm"
              />
              <MultiSelectContent className="w-64">
                <MultiSelectSearch placeholder={t`Sök eller skapa...`} />
                <MultiSelectGroup heading={t`Taggar`}>
                  <MultiSelectList>
                    {tags
                      .filter((tag) => tag.slug !== "favorited")
                      .map((tag) => (
                        <MultiSelectItem
                          key={tag.slug}
                          value={tag.name}
                          label={tag.name}
                        />
                      ))}
                  </MultiSelectList>
                </MultiSelectGroup>
              </MultiSelectContent>
            </MultiSelect>
          )}
        </div>

        {!readOnly && (
          <div className="flex items-center gap-2">
            <ButtonGroup>
              <ShareMentionCard shareUrl={mentionCard.share_url} />

              <Button
                data-testid="mark-favorite-button"
                variant="outline"
                onClick={() => {
                  mutateSetFavorites(!localIsFavorited);
                }}
              >
                <BookmarkIcon
                  className={
                    localIsFavorited
                      ? "fill-yellow-500 stroke-yellow-500"
                      : undefined
                  }
                />
                {localIsFavorited ? (
                  <Trans>Sparad</Trans>
                ) : (
                  <Trans>Spara</Trans>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    id="toggle-remove-mention"
                    data-testid="mention-action-menu-toggler"
                    size="icon"
                    variant="outline"
                  >
                    <MoreVerticalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {showReadArticle && (
                    <>
                      <DropdownMenuItem
                        data-testid="full-article-button"
                        onSelect={() => {
                          setIsContentDialogOpen(true);
                        }}
                      >
                        <NewspaperIcon />
                        <Trans>Läs hela artikeln i All Ears</Trans>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />
                    </>
                  )}

                  {showFullTranscript && (
                    <>
                      <DropdownMenuItem
                        data-testid="full-transcript-button"
                        onSelect={() => {
                          setIsContentDialogOpen(true);
                        }}
                      >
                        <AlignLeftIcon />
                        <Trans>Visa hela transkriptet</Trans>
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />
                    </>
                  )}

                  <DropdownMenuItem
                    data-testid="remove-mention-source"
                    onSelect={() => {
                      mutateMarkAsIrrelevant({ reason: "excluded_source" });
                    }}
                  >
                    <EyeOffIcon />
                    <Trans>Visa inte den här kanalen igen</Trans>
                  </DropdownMenuItem>

                  {showRequestTranscription && (
                    <DropdownMenuItem
                      disabled={isTranscribePending || transcriptionInProgress}
                      onSelect={() => {
                        mutateRequestTranscribe();
                      }}
                    >
                      <PencilLineIcon />
                      {transcriptionInProgress ? (
                        <Trans>Avsnittet transkriberas</Trans>
                      ) : (
                        <Trans>Transkribera inlägg</Trans>
                      )}
                    </DropdownMenuItem>
                  )}

                  <AccessControl level={ACCESS_LEVELS.STAFF}>
                    <DropdownMenuItem asChild>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/admin-allears/channels/episode/${mentionCard.episode.pk}`}
                      >
                        <EyeIcon />
                        <Trans>Visa avsnittet i interna verktyget</Trans>
                        <StaffBadge className="ml-auto" />
                      </a>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/internal_v2/channels/${mentionCard.channel.id}`}
                      >
                        <EyeIcon />
                        <Trans>Visa kanalen i interna verktyget</Trans>
                        <StaffBadge className="ml-auto" />
                      </a>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onSelect={async () => {
                        try {
                          await copyToClipboard(
                            `${window.location.origin}${window.location.pathname}#${
                              mentionCard.mentions[0].public_id
                            }`
                          );
                          toast.success(t`Länk kopierad`);
                        } catch {
                          toast.error(t`Lyckades inte kopiera länken`);
                        }
                      }}
                    >
                      <LinkIcon />
                      <Trans>Kopiera direktlänk</Trans>
                      <StaffBadge className="ml-auto" />
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                  </AccessControl>

                  <DropdownMenuItem
                    data-testid="remove-mention-all"
                    variant="destructive"
                    onSelect={() => {
                      mutateMarkAsIrrelevant({});
                    }}
                  >
                    <Trash2Icon />
                    <Trans>Ta bort inlägg</Trans>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          </div>
        )}
      </div>

      <Dialog open={isContentDialogOpen} onOpenChange={setIsContentDialogOpen}>
        <DialogContent data-testid="reader-modal" className="max-w-3xl!">
          <DialogHeader>
            <DialogTitle>{mentionCard.episode.title}</DialogTitle>
            <DialogDescription>
              {mentionCard.channel.name},{" "}
              {formatDate(mentionCard.episode.published_date, "PPPP")}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-4">
            <article className="flex flex-col gap-4">
              {mentionCard.channel.channel_type === SOURCE.NEWS &&
                mentionCard.episode.artwork_url && (
                  <img
                    className="rounded"
                    src={mentionCard.episode.artwork_url}
                  />
                )}

              {hasAudioMentions ? (
                <FullTranscript episodePk={mentionCard.episode.pk} />
              ) : mentionCard.text_post ? (
                <p className="text-sm leading-relaxed">
                  {mentionCard.text_post.text}
                </p>
              ) : null}
            </article>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">
                <Trans>Stäng</Trans>
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
